import type { StickerLayoutResult } from "../lib/sticker-layout.js";

/**
 * Renders a procedural sticker glyph (shape or text). SHARED by the on-video
 * overlay (CreativeOverlay) AND the sticker-picker cards (DecorateTool) so the
 * preview always matches what lands on the reel — the old picker drew an empty
 * placeholder box, which is what "no stickers shown" was.
 */
export function StickerGlyph({
  shape,
  text,
  color,
  fontSize,
  src,
}: {
  shape: StickerLayoutResult["shape"];
  text?: string;
  color: string;
  fontSize?: number | string;
  /** Curated art override (Fluent Emoji / functional SVG). Renders instead of
   *  the procedural shape below when set — shell-only, never a core asset. */
  src?: string;
}) {
  if (src) {
    return <img src={src} className="sticker-img" alt="" aria-hidden width="100%" height="100%" />;
  }
  const common = {
    width: "100%",
    height: "100%",
    viewBox: "0 0 48 48",
    preserveAspectRatio: "xMidYMid meet" as const,
    "aria-hidden": true as const,
  };
  switch (shape) {
    case "dot":
      return (
        <svg {...common}>
          <circle cx="24" cy="24" r="10" fill={color} />
        </svg>
      );
    case "star":
      return (
        <svg {...common}>
          <polygon fill={color} points="24,4 29,18 44,18 32,28 36,42 24,33 12,42 16,28 4,18 19,18" />
        </svg>
      );
    case "burst":
      return (
        <svg {...common}>
          <polygon
            fill={color}
            opacity="0.92"
            points="24,2 28,16 42,12 32,24 44,30 30,32 32,46 24,36 16,46 18,32 4,30 16,24 6,12 20,16"
          />
        </svg>
      );
    case "arrow":
      return (
        <svg {...common} viewBox="0 0 64 28">
          <path fill={color} d="M4 14h34v-8l18 8-18 8v-8H4z" />
        </svg>
      );
    case "underline":
      return (
        <svg {...common} viewBox="0 0 64 16">
          <rect x="2" y="5" width="60" height="6" rx="2" fill={color} />
        </svg>
      );
    case "frame":
      return (
        <svg {...common} viewBox="0 0 64 48">
          <rect x="3" y="3" width="58" height="42" fill="none" stroke={color} strokeWidth="4" />
        </svg>
      );
    case "badge":
      return (
        <span className="creative-sticker-badge" style={{ background: color, fontSize }}>
          {text || "!"}
        </span>
      );
    case "text":
    default:
      return (
        <span className="creative-sticker-text" style={{ fontSize, color }}>
          {text || "!"}
        </span>
      );
  }
}
